import { Dimensions, Image, Platform } from 'react-native';

interface ImageDimensions {
  width: number;
  height: number;
  calculatedHeight: number;
}

// Global cache for image dimensions
const dimensionsCache = new Map<string, ImageDimensions>();

const MIN_HEIGHT = 150;
const MAX_HEIGHT = Platform.OS === 'web' ? 400 : 350;

/**
 * Calculate the display height for an image based on container width and aspect ratio
 */
function calculateDisplayHeight(
  imageWidth: number,
  imageHeight: number,
  containerWidth: number
): number {
  const aspectRatio = imageHeight / imageWidth;
  const calculatedHeight = containerWidth * aspectRatio;
  return Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, calculatedHeight));
}

/**
 * Get cached image dimensions or fetch and cache them
 */
export async function getImageDimensions(
  uri: string,
  containerPadding: number = 32
): Promise<number> {
  // Check cache first
  const cached = dimensionsCache.get(uri);
  if (cached) {
    return cached.calculatedHeight;
  }

  const screenWidth = Dimensions.get('window').width;
  // On web, content is constrained to max 680px width, so use that for calculation
  const effectiveWidth = Platform.OS === 'web'
    ? Math.min(screenWidth, 680)
    : screenWidth;
  const containerWidth = effectiveWidth - containerPadding;

  return new Promise((resolve) => {
    if (Platform.OS === 'web') {
      const img = new (globalThis as any).Image();
      img.onload = () => {
        const height = calculateDisplayHeight(
          img.naturalWidth,
          img.naturalHeight,
          containerWidth
        );
        dimensionsCache.set(uri, {
          width: img.naturalWidth,
          height: img.naturalHeight,
          calculatedHeight: height,
        });
        resolve(height);
      };
      img.onerror = () => resolve(200);
      img.src = uri;
    } else {
      Image.getSize(
        uri,
        (width, height) => {
          const calculatedHeight = calculateDisplayHeight(width, height, containerWidth);
          dimensionsCache.set(uri, {
            width,
            height,
            calculatedHeight,
          });
          resolve(calculatedHeight);
        },
        () => resolve(200)
      );
    }
  });
}

/**
 * Get cached dimensions synchronously (returns null if not cached)
 */
export function getCachedImageHeight(uri: string): number | null {
  const cached = dimensionsCache.get(uri);
  return cached?.calculatedHeight ?? null;
}

/**
 * Preload and cache image dimensions for a list of URIs
 */
export async function preloadImageDimensions(uris: string[]): Promise<void> {
  await Promise.all(uris.map((uri) => getImageDimensions(uri)));
}

/**
 * Clear the dimensions cache (useful for memory management)
 */
export function clearDimensionsCache(): void {
  dimensionsCache.clear();
}
